import { useMemo, type ReactNode } from 'react'
import { Toaster, toast as sonner } from 'sonner'
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react'
import clsx from 'clsx'

/**
 * Toasts.
 *
 * The behaviour contract is unchanged from the hand-rolled version this
 * replaces, and it is the part that matters during a service:
 *
 *   - announced through an `aria-live` region, so a screen reader hears
 *     "Booking confirmed" without the focus moving off the row being worked on
 *   - errors PERSIST until dismissed; successes and notices auto-dismiss.
 *     A message somebody has to act on must never disappear on a timer
 *   - stacked above the phone's bottom navigation, so it never covers the five
 *     primary actions
 *
 * What Sonner adds that the hand-rolled version did not have, and would have
 * been a few hundred lines to get right:
 *
 *   - swipe to dismiss, with velocity: a flick counts even if it is short
 *   - a real stack. Three messages arriving during a rush collapse into a pile
 *     that expands on hover instead of pushing each other around the screen
 *   - timers that pause when the tab is hidden or the pointer is over the
 *     stack, so a toast is not spent while the phone is in an apron pocket
 *   - interruptible transitions. Toasts arrive in bursts here — confirm, seat,
 *     complete — and CSS transitions retarget mid-flight where keyframes would
 *     restart from zero and stutter
 *
 * The markup stays ours. `toast.custom` means Sonner supplies the machinery
 * and this file supplies the card, so a toast still looks like the rest of the
 * product rather than like a library's default.
 */

export type ToastTone = 'success' | 'error' | 'info'

interface ToastApi {
  success: (message: string, detail?: string) => void
  error: (message: string, detail?: string) => void
  info: (message: string, detail?: string) => void
  dismiss: (id: string | number) => void
}

/** Matches the old provider: a success is spent after five seconds. */
const AUTO_DISMISS_MS = 5000

function push(tone: ToastTone, message: string, detail?: string) {
  sonner.custom((id) => <ToastCard id={id} tone={tone} message={message} detail={detail} />, {
    // An error is a thing to be dealt with, not a notification. It stays until
    // somebody dismisses it.
    duration: tone === 'error' ? Number.POSITIVE_INFINITY : AUTO_DISMISS_MS,
  })
}

/**
 * The API, as a module constant.
 *
 * Sonner's queue is global, so unlike the previous implementation there is no
 * React state to reach and therefore no context to thread. The hook and the
 * provider survive unchanged because eighteen call sites already use them, and
 * a rename across all of them would be churn with no reader on the other end.
 */
const api: ToastApi = {
  success: (message, detail) => push('success', message, detail),
  error: (message, detail) => push('error', message, detail),
  info: (message, detail) => push('info', message, detail),
  dismiss: (id) => sonner.dismiss(id),
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <Toaster
        // Bottom right on a desktop, where it is out of the way of the diary.
        // On anything under 600px Sonner goes full width by itself and only
        // the vertical anchor applies, which is the behaviour we want: a
        // 356px card floating against one edge of a 375px phone looks like it
        // missed.
        position="bottom-right"
        // Three, as before. A fourth arriving during a rush pushes the first
        // out rather than building a wall over the bookings.
        visibleToasts={3}
        // Our own card, so Sonner's default skin never paints.
        toastOptions={{ unstyled: true, classNames: { toast: 'w-full' } }}
        offset={{ bottom: '24px', right: '24px' }}
        // Clears the bottom navigation AND the iOS home indicator. The nav is
        // ~66px plus its own safe-area padding; 5.5rem is the same figure the
        // shell reserves for it in `main`'s bottom padding, so the two cannot
        // drift apart.
        mobileOffset={{ bottom: 'calc(5.5rem + var(--safe-bottom))', left: '12px', right: '12px' }}
        containerAriaLabel="Notifications"
        // Sonner ships `z-index: 999999999`, which is not a number anybody
        // chose — it is the number you pick when there is no scale. There is
        // one here, and a toast's place on it is above the dialog it reports
        // the outcome of and below nothing.
        style={{ zIndex: 'var(--z-toast)' }}
      />
    </>
  )
}

function ToastCard({
  id,
  tone,
  message,
  detail,
}: {
  id: string | number
  tone: ToastTone
  message: string
  detail?: string
}) {
  return (
    <div
      // Errors interrupt; everything else waits its turn. Sonner's own
      // container is `aria-live="polite"`, which is right for a confirmation
      // and too quiet for "that booking was not saved".
      role={tone === 'error' ? 'alert' : 'status'}
      className={clsx(
        'flex w-full max-w-md items-start gap-3 rounded-[var(--radius-lg)] border bg-surface px-4 py-3 shadow-lg',
        tone === 'success' && 'border-success-600/25',
        tone === 'error' && 'border-danger-500/30',
        tone === 'info' && 'border-line-strong',
      )}
    >
      <ToneIcon tone={tone} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{message}</p>
        {detail ? (
          <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">{detail}</p>
        ) : null}
      </div>
      {/* 44px on a touch screen, not the 28px this was. An error toast never
          expires on its own and up to three stack, so on a phone mid-service
          this button is the only way to clear a card sitting over the diary —
          and at 28px it was the one control on that card a thumb missed. The
          negative margins keep the glyph optically where it was while the hit
          area grows around it. */}
      <button
        type="button"
        onClick={() => sonner.dismiss(id)}
        className="press -mr-2.5 -mt-2.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700 [@media(pointer:fine)]:-mr-1 [@media(pointer:fine)]:-mt-1 [@media(pointer:fine)]:h-7 [@media(pointer:fine)]:w-7"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  )
}

function ToneIcon({ tone }: { tone: ToastTone }) {
  const common = 'mt-0.5 h-5 w-5 shrink-0'
  if (tone === 'success') {
    return <CircleCheck className={clsx(common, 'text-success-600')} strokeWidth={2} aria-hidden="true" />
  }
  if (tone === 'error') {
    return <CircleAlert className={clsx(common, 'text-danger-600')} strokeWidth={2} aria-hidden="true" />
  }
  return <Info className={clsx(common, 'text-info-600')} strokeWidth={2} aria-hidden="true" />
}

export function useToast(): ToastApi {
  // Stable across renders, so it stays safe in a `useCallback`/`useEffect`
  // dependency list — which several call sites rely on.
  return useMemo(() => api, [])
}
